import { h, Fragment } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { getBundleProducts } from 'utils/storefront/get-bundle-products';
import TrapFocus from 'utils/trap-focus';
import ProductPrice from 'utils/product-price';
import updateURL from 'utils/update-url';
import setAttributes from 'utils/set-attributes';
import { Close } from 'utils/icon/icons';
import { formatProduct } from 'utils/storefront/format-resource';
import { getLinkedProduct } from 'utils/storefront/get-linked-products';
import { trackQuickViewATBClick, trackQuickViewFullPage } from 'utils/tracking';
import ProductDetails from '../product/product-details';
import VariantSelector from '../product/variant-selector';
import ProductQuantity from '../product/product-quantity';
import Reviews from '../product/yotpo-reviews';
import MaxOrderMessage from '../product/max-order-message';
import { Klarna } from '../product/klarna';
import { KeyDetails } from '../product/details-with-flyout-panel';
import ProductGallery from '../product/product-gallery';
import { InventoryMessage, showInventoryMessageFn } from '../product/inventory-message';

const ProductInfo = ({
  product,
  isDesktop,
  currentVariant,
  setCurrentVariant,
  bundle,
  isBundlePlp,
  onClose,
  currentProduct,
  maxOrderQTY,
  shoppayMessage,
  keyIngredients,
  backInStockData,
  inventoryMessageData
}) => {
  const [qty, setQty] = useState(1);

  const AddToCartButton = () => {
    const isNotForSale = currentProduct?.tags?.includes('not-for-sale');
    
    if (isNotForSale) {
      return null;
    }

    const hasGiftCardTag = (tags) => {
      const hasTag = tags && tags.includes('gift_card');
      return !!hasTag;
    };

    const handleAddToCart = (e, hasUrl) => {
      e.preventDefault();
      const url = `${Shopify.routes.root}products/${currentProduct.handle}`;
      if (hasUrl) {
        window.location.href = url;
        return;
      }

      let productPayload;

      if (bundle) {
        productPayload = window.getBundlePayload(bundle, product, product.variants[0], qty);
      } else {
        const attributes = setAttributes(currentProduct);

        productPayload = {
          ...window.getPayload(currentProduct.variants[0], qty),
          attributes,
        };
      }

      trackQuickViewATBClick({
        item: product.title,
        itemId: product.variants[0].sku,
        size:
          currentVariant.selectedOptions[0]?.name?.toLowerCase() === 'size'
            ? currentVariant.selectedOptions[0].value
            : '',
        quantity: qty,
        color:
          currentVariant.selectedOptions[0]?.name?.toLowerCase() === 'color'
            ? currentVariant.selectedOptions[0].value
            : '',
      });

      const addToCartEvent = new CustomEvent('cart:add', {
        detail: {
          payload: productPayload,
        },
      });
      document.dispatchEvent(addToCartEvent);
      setTimeout(() => {
        onClose();
      }, 150);
    };

    let buttonText;

    const isAvailable = bundle ? bundle?.available : currentVariant?.availableForSale;

    if (bundle) {
      buttonText = bundle.available ? window.TMW.Strings.product.addToCart : window.TMW.Strings.product.unavailable;
    }

    if (!bundle) {
      buttonText = currentVariant
        ? currentVariant?.availableForSale
          ? window.TMW.Strings.product.addToCart
          : window.TMW.Strings.product.outOfStock
        : window.TMW.Strings.product.unavailable;
    }

    const openBisModal = (event) => {
      const modal = event.target.closest(".quick-view").querySelector('.bis-modal');
      modal.classList.remove('hidden');
    }

    return (isAvailable || !backInStockData.enabled) ? (
      <button
        type="submit"
        className="atc-button button button--primary w-full h-[50px]"
        disabled={!isAvailable}
        onClick={(e) => handleAddToCart(e, hasGiftCardTag(currentProduct.tags))}
      >
        {hasGiftCardTag(currentProduct.tags) ? window.TMW.Strings.product.viewDetails : buttonText}
      </button>
    ) : (
      <button
        type="button"
        className="button button--primary w-full h-[50px] py-4 klaviyo-bis-trigger"
        data-klaviyo-bis="true"
        onClick={openBisModal}
      >
        {backInStockData.atcText}
      </button>
    );
  };

  const BackInStockModal = () => {
    const currentVariantId = currentVariant.id.split("/").pop();

    const handleBisModalClose = (event) => {
      const modal = event.target.closest(".bis-modal")
      modal.classList.add('hidden');
    }

    const createBisSubscription = async (email, variantId) => {
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${backInStockData.klaviyoAccount}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2025-01-15'
            },
            body: JSON.stringify(payload)
          }
        );

        return response.ok;

      } catch (error) {
        // console.error('Klaviyo BIS Error:', error);
        return false;
      }
    }

    const handleBisSubmit = async (event) => {
      event.preventDefault();
      const email = event.target.querySelector('.bis-email').value;
      const successText = event.target.parentElement.querySelector('.bis-success');
      const failureText = event.target.parentElement.querySelector('.bis-error');

      const success = await createBisSubscription(email, currentVariantId);

      successText.classList.toggle('hidden', !success);
      failureText.classList.toggle('hidden', success);
      event.target.classList.toggle('hidden', success);
    };

    return (
      <div className="bis-modal hidden fixed inset-0 bg-black bg-opacity-50 z-50">
        <div className="flex items-center justify-center h-full">
          <div className="relative bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <button
              className="absolute top-2.5 right-[15px] w-6 h-6"
              aria-label={window.TMW.Strings.icons.close}
              title={window.TMW.Strings.icons.close}
              tabIndex="0"
              onClick={handleBisModalClose}
            >
              <Close />
            </button>
            <h2 className="text-xl mb-4">{backInStockData.modalHeading}</h2>
            <p className="mb-4">{backInStockData.modalSubheading}</p>
            <form className="bis-form space-y-4" onSubmit={handleBisSubmit}>
              <input
                type="email"
                placeholder="Email"
                required
                className="bis-email w-full p-2 border border-gray-300 rounded"
              />
              <button
                type="submit"
                className="button button--primary w-full p-2 rounded"
              >
                {backInStockData.modalCtaText}
              </button>
            </form>
            <div className="bis-success hidden text-green-600 mt-4">
              {backInStockData.successText}
            </div>
            <div className="bis-error hidden text-red-600 mt-4">
              {backInStockData.failureText}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const maxOrderMessage = useMemo(
    () => (
      <MaxOrderMessage maxOrder={maxOrderQTY?.max_order_qty} errorMessage={maxOrderQTY?.error_message} quantity={qty} />
    ),
    [maxOrderQTY?.max_order_qty, maxOrderQTY?.error_message, qty],
  );

  const showInventoryMessage = showInventoryMessageFn(currentVariant);

  const handleLinkClick = (e) => {
    e.preventDefault();
    trackQuickViewFullPage({
      item: currentProduct.title,
      itemId: currentVariant.sku,
    });
    window.location = updateURL(`${Shopify.routes.root}products/${currentProduct.handle}`);
  };

  return (
    <div className="mt-2.5 lg:mt-0 lg:pl-5 w-full">
      {isDesktop && (
        <div>
          <h2 className="leading-9 tracking-tighter mb-[18px] mt-2">{currentProduct.title}</h2>
          {!currentProduct?.tags?.includes('not-for-sale') && (
            <span className="block text-product-tag lg:mb-[14px] font-medium">
              <ProductPrice product={currentProduct} />
            </span>
          )}
        </div>
      )}
      {product.subtitle && (
        <>
          {(() => {
            const productSubtitleLowercase = currentProduct.subtitle.toLowerCase();
            // eslint-disable-next-line max-len
            const capitalizedSubtitle =
              productSubtitleLowercase.charAt(0).toUpperCase() + productSubtitleLowercase.slice(1);
            return <p className={`title-3 ${showInventoryMessage ? 'mb-2' : 'mb-3 lg:mb-4'}`}>{capitalizedSubtitle}</p>;
          })()}
        </>
      )}{' '}
      {showInventoryMessage && <InventoryMessage inventoryMessageData={inventoryMessageData} />}
      <Reviews id={product.id.split('Product/')[1]} />
      {bundle && (
        <div className="pb-5 border-b border-grey-4 mt-2.5 lg:mt-2">
          <span className="flex items-center lg:mb-1">
            <span className="text-body-2 font-medium">{window.TMW.Strings.bundle.bundleTitle}</span>
            <span className="text-body-2 font-medium">&nbsp;({bundle.products?.length})</span>
          </span>
          {bundle.products.map((prod, index) => (
            <span className="text-body-2" key={`prod-${index}`}>
              {index === bundle.products.length - 2
                ? `${prod.title} & `
                : index === bundle.products.length - 1
                ? `${prod.title}`
                : `${prod.title}, `}
            </span>
          ))}
        </div>
      )}
      {currentProduct.singleBundleItems && (
        <div className="pb-5 border-b border-grey-4 mt-2.5 lg:mt-2">
          <span className="flex items-center lg:mb-1">
            <span className="text-body-2 font-medium">{window.TMW.Strings.bundle.bundleTitle}</span>
            <span className="text-body-2 font-medium">&nbsp;({currentProduct.singleBundleItems?.length})</span>
          </span>
          {currentProduct.singleBundleItems.map((title, index) => (
            <span className="text-body-2" key={`prod-${index}`}>
              {index === currentProduct.singleBundleItems.length - 2
                ? `${title} & `
                : index === currentProduct.singleBundleItems.length - 1
                ? `${title}`
                : `${title}, `}
            </span>
          ))}
        </div>
      )}
      <ProductDetails product={currentProduct} isQuickView={true} isBundlePlp={isBundlePlp} />
      {isBundlePlp && (
        <KeyDetails
          detailsFlyout={{
            flyoutTitle: 'Key Ingredients',
            productIcons: keyIngredients,
            isBundlePlp,
          }}
        />
      )}
      {!isBundlePlp && !bundle && product.options[0].name !== 'Title' && (
        <div className="pt-3 pb-4">
          {product?.options?.map((option) => (
            <VariantSelector
              isQuickView={true}
              currentVariant={currentVariant}
              product={product}
              key={option.name}
              option={option}
              setCurrentVariant={setCurrentVariant}
            />
          ))}
        </div>
      )}
      {!isBundlePlp && (
        <div className="flex mt-5">
          {!currentProduct?.tags?.includes('not-for-sale') && (
            <ProductQuantity
              quantity={qty}
              changeQuantity={(value) => setQty(value)}
              maxOrder={maxOrderQTY?.max_order_qty}
            />
          )}
          <AddToCartButton currentVariant={currentVariant} />
        </div>
      )}
      {window.TMW.localization.countryCode === 'CA' && !isBundlePlp && !currentProduct?.tags?.includes('not-for-sale') && <Klarna price={currentVariant.price} />}
      {maxOrderMessage}
      {shoppayMessage && !isBundlePlp && (
        <div className="text-body-3 mb-5 mt-2" dangerouslySetInnerHTML={{ __html: shoppayMessage }} />
      )}
      {!isBundlePlp && (
        <a
          href={`${Shopify.routes.root}products/${currentProduct.handle}`}
          className="text-body-2 underline"
          onClick={(e) => {
            handleLinkClick(e);
          }}
        >
          {window.TMW.Strings.product.viewPage}
        </a>
      )}
      {backInStockData.enabled && <BackInStockModal />}
    </div>
  );
};

const QuickView = ({ data }) => {
  const storefront = Storefront.getInstance();
  const [productData, setProductData] = useState(null);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [groupProduct, setGroupProduct] = useState(null);
  const [shoppayMessage, setShoppayMessage] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [isBundle, setIsBundle] = useState(null);
  const [isOpened, setIsOpened] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [keyIngredients, setKeyIngredients] = useState(null);

  const url = new URL(window.location.href);
  const isBundlePlp = url.pathname.includes('bundle-builder');

  const isDesktop = data.layout.bps.above.lg.matches;

  const quickView = {
    open: (handle) => {
      const fetchData = async () => {
        setProductData(await getProductData(handle));
      };
      fetchData();
    },
  };

  const handleExternalQuickView = (e) => {
    quickView.open(e.detail.payload);
  };

  useEffect(() => {
    document.addEventListener('quick-view:open', handleExternalQuickView);
    return () => document.removeEventListener('quick-view:open', handleExternalQuickView);
  }, []);

  const getProductData = async (handle) => {
    const response = await storefront.request({
      query: queries.productByHandleGet,
      variables: {
        handle,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', response);
      return [];
    }

    return {
      product: formatProduct(response?.data?.productByHandle, 'product'),
      products: [formatProduct(response?.data?.productByHandle, 'product')],
    };
  };

  const getIngredientData = async (GID) => {
    let title = await storefront.request({
      query: queries.metaObjectByGIDGet(GID, 'product_icon_title'),
    });

    let shortDescription = await storefront.request({
      query: queries.metaObjectByGIDGet(GID, 'short_description'),
    });

    let image = await storefront.request({
      query: queries.metaObjectByGIDGet(GID, 'product_icon_image'),
    });

    if (!title.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', title);
    }

    if (!shortDescription.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', shortDescription);
    }

    if (!image.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', image);
    }

    if (!title.data || !shortDescription.data || !image.data) return {};

    title = title.data.metaobject.field.value;
    shortDescription = shortDescription.data.metaobject.field.value;
    image = image.data.metaobject.field.value;

    return { title, shortDescription, image };
  };

  const getImageUrl = async (GID) => {
    const response = await storefront.request({
      query: queries.imageUrlByGIDGet(GID),
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', response);
      return [];
    }

    return response.data.node.image.url;
  };

  const getProductKeyIngredients = async (handle) => {
    const response = await storefront.request({
      query: queries.metafieldByHandleGet(handle, 'details', 'product_icons'),
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error('quick-view.js: Storefront API request failed', response);
      return [];
    }

    const metaobjectGlobalIds = response.data.productByHandle.metafield.value.split(',');

    const keyIngredientsData = [];

    for (const GID of metaobjectGlobalIds) {
      const cleanedGid = GID.replace('[', '').replace(/"/g, '').replace(']', '');

      // eslint-disable-next-line
      const ingredient = await getIngredientData(cleanedGid.split('/')[4]);

      // eslint-disable-next-line
      const imageUrl = await getImageUrl(ingredient.image.split('/')[4]);

      const imgElement = `<img src="${imageUrl}">`;

      ingredient.image = imgElement;
      keyIngredientsData.push(ingredient);
    }

    return keyIngredientsData;
  };

  useEffect(async () => {
    if (isOpened) {
      const ingredients = await getProductKeyIngredients(currentProduct.handle);
      setKeyIngredients(ingredients);
    }
  }, [isOpened]);

  useEffect(() => {
    const initQuickView = () => {
      setCurrentProduct(productData.product);
      setCurrentVariant(productData.product.variants[0]);
      setIsOpened(true);
      setTimeout(() => {
        setIsLoaded(true);
      }, 100);
      window.yotpoWidgetsContainer?.initWidgets();
      window.Klarna?.OnsiteMessaging?.refresh();
    };

    if (!productData) return;

    if (!productData.product.tags?.length) {
      initQuickView();
    }

    if (productData.product.tags?.length) {
      const groupTag = productData.product.tags.find((tag) => tag.includes('group::'));
      const bundleTag = productData.product.tags.find((tag) => tag === 'bundle');
      const giftCart = productData.product.tags.find((tag) => tag.includes('gift_cart'));

      // grouped product
      if (groupTag) setGroupProduct(groupTag);

      // bundle
      if (bundleTag) setIsBundle(bundleTag);

      // giftCart
      if (giftCart) setIsBundle(giftCart);

      if (!groupTag && !bundleTag) initQuickView();
    }

    const fetchShopPay = async () => {
      let shoppay = await fetch(
        // eslint-disable-next-line max-len
        `${Shopify.routes.root}products/${productData.product.handle}?view=shoppay`,
      );
      shoppay = await shoppay.text();
      return shoppay;
    };

    if (window.TMW.localization.countryCode === 'US') {
      fetchShopPay().then((res) => {
        // To prevent 404 from filling dom, search for shop-pay tag
        // and only set if it exists
        if (!res.includes('shoppay-init')) return;
        setShoppayMessage(res);
      });
    }
  }, [productData]);

  // Grouped / Bundle
  useEffect(() => {
    if (groupProduct || isBundle) {
      setCurrentProduct(productData.product);
      setCurrentVariant(productData.product.variants[0]);

      const fetchGroupData = async () => {
        const response = await getLinkedProduct(groupProduct);
        setProductData(response);
        setIsLoaded(true);
      };

      const fetchBundleData = async () => {
        setBundle(await getBundleProducts(productData.product));
        setIsLoaded(true);
      };

      if (groupProduct) {
        // eslint-disable-next-line no-console
        fetchGroupData().catch(console.error);
      }

      if (isBundle) {
        // eslint-disable-next-line no-console
        fetchBundleData().catch(console.error);
      }

      setIsOpened(true);
      window.yotpoWidgetsContainer?.initWidgets();
    }
  }, [groupProduct, isBundle]);

  const handleClose = () => {
    setIsOpened(null);
    setIsLoaded(false);
    setIsBundle(null);
    setBundle(null);
    setGroupProduct(false);
    setKeyIngredients(null);
  };

  useEffect(() => {
    if (productData && currentVariant) {
      const currentProd = productData.products.find((p) => p.variants.find((v) => v.id === currentVariant.id));

      setCurrentProduct(currentProd);
    }
  }, [currentVariant]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpened) {
      if (!trapFocus) trapFocus = new TrapFocus(document.querySelector('.quick-view'));
      document.body.classList.add('no-scroll');
      trapFocus.init(true);

      document.querySelector('.quick-view__overlay').addEventListener('click', handleClose);
    }

    return () => {
      document.body.classList.remove('no-scroll');
      if (trapFocus) trapFocus.destroy(document.querySelector(`[data-handle=${productData.product.handle}]`));
    };
  }, [isOpened, trapFocus]);

  return (
    isOpened && (
      <>
        <div
          className={`quick-view fixed bottom-0 left-0 right-0 bg-white 
                      px-5 pt-10 pb-8 z-[1000] 
                      lg:bottom-auto lg:top-1/2 lg:right-auto lg:left-1/2 
                      lg:-translate-x-1/2 lg:-translate-y-1/2 lg:p-10 
                      lg:w-[934px] max-h-full transition-opacity
                       overflow-y-auto duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0 transition-all'}`}
        >
          <button
            className="absolute top-2.5 right-[15px] quick-view__button w-6 h-6"
            aria-label={window.TMW.Strings.icons.close}
            title={window.TMW.Strings.icons.close}
            tabIndex="0"
            onClick={handleClose}
          >
            <Close />
          </button>

          {!isDesktop && (
            <div className={`duration-300 transition-opacity ${isLoaded ? 'opacity-100' : 'opacity-0 transition-all'}`}>
              <div className="flex flex-col lg:flex-row">
                <ProductGallery actualProduct={currentProduct} isQuickview={true} />
                <div>
                  <h2 className="pt-1 leading-9 tracking-tighter">{currentProduct.title}</h2>
                  {!currentProduct?.tags?.includes('not-for-sale') && (
                    <span className="text-product-tag">
                      <ProductPrice product={currentProduct} />
                    </span>
                  )}
                </div>
              </div>
              <ProductInfo
                product={productData.product}
                isDesktop={isDesktop}
                currentVariant={currentVariant}
                setCurrentVariant={setCurrentVariant}
                bundle={bundle}
                currentProduct={currentProduct}
                onClose={handleClose}
                maxOrderQTY={data.maxOrderQTY}
                shoppayMessage={shoppayMessage}
                isBundlePlp={isBundlePlp}
                keyIngredients={keyIngredients}
                backInStockData={data.backInStockData}
              />
            </div>
          )}

          {isDesktop && (
            <div className={`flex transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
              {/* <ProductBadge
                tags={currentProduct.tags}
                className={
                  'absolute bg-pantone-7 py-1 px-2.5 top-12 z-1 text-product-tag leading-6'
                }
              /> */}
              <ProductGallery actualProduct={currentProduct} isQuickview={true} />
              <ProductInfo
                product={productData.product}
                isDesktop={isDesktop}
                currentVariant={currentVariant}
                setCurrentVariant={setCurrentVariant}
                bundle={bundle}
                currentProduct={currentProduct}
                onClose={handleClose}
                maxOrderQTY={data.maxOrderQTY}
                shoppayMessage={shoppayMessage}
                isBundlePlp={isBundlePlp}
                keyIngredients={keyIngredients}
                backInStockData={data.backInStockData}
              />
            </div>
          )}
        </div>
        <div className="quick-view__overlay" />
      </>
    )
  );
};

export default QuickView;
